import { Component, Input, OnChanges, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Config, DocumentType, FileType } from '@onlyoffice/doceditor-types';
import { environment } from '../../../../environments/environment';

const CONTENT_TYPE_TO_EXT: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.oasis.opendocument.text': 'odt',
  'application/vnd.oasis.opendocument.spreadsheet': 'ods',
  'application/vnd.oasis.opendocument.presentation': 'odp',
  'application/rtf': 'rtf',
  'text/rtf': 'rtf',
  'text/plain': 'txt',
  'text/csv': 'csv',
};

const WORD_EXTS = ['doc', 'docx', 'docm', 'dot', 'dotx', 'odt', 'ott', 'rtf', 'txt', 'epub', 'fb2', 'html', 'mht', 'xml'];
const CELL_EXTS = ['xls', 'xlsx', 'xlsm', 'xlt', 'xltx', 'ods', 'ots', 'csv'];
const SLIDE_EXTS = ['ppt', 'pptx', 'pptm', 'pot', 'potx', 'odp', 'otp', 'pps', 'ppsx'];

@Component({
    selector: 'app-pdf-preview',
    templateUrl: './pdf-preview.component.html',
    standalone: false
})
export class PdfPreviewComponent implements OnChanges {

  @Input() pdfUrl: string = '';
  @Input() fileContentType: string = '';
  @Input() fileName: string = '';
  @Input() isStaffingdomain: boolean = false;

  displayUrl!: SafeResourceUrl;
  isPdf = false;
  isOfficeDoc = false;
  isLoading = true;

  onlyOfficeServerUrl = environment.onlyOfficeServerUrl;
  editorConfig: Config | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnChanges(): Promise<void> {
    if (!this.pdfUrl) return;

    this.isLoading = true;

    const ext = this.resolveExtension();
    const documentType = this.getDocumentType(ext);
    const iframeUrl = this.getIframePreviewUrl(this.pdfUrl);
    if (documentType && ext !== 'pdf') {
      this.isPdf = false;
      this.isOfficeDoc = true;
      this.editorConfig = this.buildConfig(ext, documentType);
    } else {
      this.isPdf = true;
      this.isOfficeDoc = false;
      this.editorConfig = null;
      this.displayUrl =
        this.sanitizer.bypassSecurityTrustResourceUrl(iframeUrl);
    }

    this.cdr.markForCheck();
  }

  onIframeLoad(): void {
    setTimeout(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    }, 0);
  }

  onDocumentReady = (): void => {
    setTimeout(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    }, 0);
  };

  onEditorError = (error: any): void => {
    console.log('ONLYOFFICE editor error', error);
    this.isLoading = false;
    this.cdr.detectChanges();
  };

  private resolveExtension(): string {
    const contentType = (this.fileContentType || '').split(';')[0].trim().toLowerCase();
    if (contentType && CONTENT_TYPE_TO_EXT[contentType]) {
      return CONTENT_TYPE_TO_EXT[contentType];
    }
    const fromName = this.getExtension(this.fileName);
    if (fromName) return fromName;
    return this.getExtensionFromUrl(this.pdfUrl);
  }

  private getDocumentType(ext: string): string {
    if (ext === 'pdf') return 'pdf';
    if (WORD_EXTS.includes(ext)) return 'word';
    if (CELL_EXTS.includes(ext)) return 'cell';
    if (SLIDE_EXTS.includes(ext)) return 'slide';
    return '';
  }

  private buildConfig(ext: string, documentType: string): Config {
    return {
      type: 'embedded',
      documentType: documentType as DocumentType,
      document: {
        fileType: ext as FileType,
        key: this.generateKey(this.pdfUrl),
        title: this.fileName || `document.${ext}`,
        url: this.pdfUrl,
        permissions: {
          edit: false,
          download: true,
          print: true,
          copy: true,
          comment: false,
          fillForms: false,
          review: false,
        },
      },
      editorConfig: {
        mode: 'view',
        lang: 'en',
        customization: {
          anonymous: { request: false },
          chat: false,
          comments: false,
          plugins: false,
          help: false,
          feedback: false,
          toolbarNoTabs: true,
          hideRulers: true,
          hideRightMenu: true,
          compactHeader: true,
        } as any,
      },
    };
  }

  private generateKey(url: string): string {
    let hash = 5381;
    for (let i = 0; i < url.length; i++) {
      hash = ((hash << 5) + hash + url.charCodeAt(i)) >>> 0;
    }
    return 'doc' + hash.toString(36) + url.length.toString(36);
  }

  private getExtension(name: string): string {
    if (!name || !name.includes('.')) return '';
    return name.split('.').pop()?.toLowerCase() || '';
  }

   getIframePreviewUrl(url: string): string {
    if (!this.isStaffingdomain) {
      return url;
    }

    const hasHash = url.includes('#');
    const toolbarParams = 'toolbar=0&navpanes=0';
    return `${url}${hasHash ? '&' : '#'}${toolbarParams}`;
  }

   getExtensionFromUrl(url: string): string {
    try {
      const pathname = new URL(url).pathname;
      const last = pathname.split('/').pop() || '';
      return this.getExtension(last);
    } catch {
      return '';
    }
  }
}
